import React from 'react';
import { Card, CardContent } from './ui/card';
import { Shield, Target, Users, BookOpen, CheckCircle, TrendingUp, Globe, Award } from 'lucide-react';
import { philosophyPoints, benefits } from '../mock';

export const About = () => {
  return (
    <section id="about" className="py-20 bg-gradient-to-b from-white to-orange-50">
      <div className="container mx-auto px-4">
        {/* Zigzag Layout - Header */}
        <div className="grid lg:grid-cols-12 gap-12 items-center mb-20">
          <div className="lg:col-span-7 lg:order-1">
            <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 bg-orange-100 px-4 py-2 rounded-full">
                <Globe className="h-5 w-5 text-orange-600" />
                <span className="text-orange-700 font-semibold">About Our Academy</span>
              </div>
              <h2 className="text-5xl font-black text-gray-900 leading-tight">
                Premier Oil & Gas <br />
                <span className="bg-gradient-to-r from-orange-600 to-yellow-600 bg-clip-text text-transparent">
                  Training Institute
                </span><br />
                in Gujarat, India
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Leading well control training academy committed to elevating rig-site safety by equipping non-drilling personnel with 
                specialized knowledge, situational awareness, and practical skills for safe drilling operations and emergency response.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5 lg:order-2">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-orange-200 to-yellow-200 rounded-3xl opacity-20"></div>
              <div className="relative bg-white p-8 rounded-3xl shadow-xl border-2 border-orange-100">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center space-y-2">
                    <TrendingUp className="h-12 w-12 text-orange-600 mx-auto" />
                    <div className="text-2xl font-bold text-gray-900">24/7</div>
                    <div className="text-sm text-gray-600">Training Support</div>
                  </div>
                  <div className="text-center space-y-2">
                    <Award className="h-12 w-12 text-yellow-600 mx-auto" />
                    <div className="text-2xl font-bold text-gray-900">Global</div>
                    <div className="text-sm text-gray-600">Standards</div>
                  </div>
                  <div className="text-center space-y-2">
                    <Shield className="h-12 w-12 text-orange-600 mx-auto" />
                    <div className="text-2xl font-bold text-gray-900">Virtual</div>
                    <div className="text-sm text-gray-600">Training</div>
                  </div>
                  <div className="text-center space-y-2">
                    <CheckCircle className="h-12 w-12 text-green-600 mx-auto" />
                    <div className="text-2xl font-bold text-gray-900">2 Years</div>
                    <div className="text-sm text-gray-600">Certification</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Zigzag Layout - Mission & Vision */}
        <div className="grid lg:grid-cols-12 gap-12 items-center mb-20">
          <div className="lg:col-span-5 lg:order-2">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <Shield className="h-6 w-6 text-orange-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Who We Are</h3>
                </div>
                <p className="text-gray-600 leading-relaxed pl-15">
                  Phoenix WellControl Academy is India's premier oil and gas training institute specializing in well control certification, 
                  drilling safety courses, and rig site safety training. We are committed to elevating drilling operation safety 
                  by equipping non-drilling personnel with comprehensive well control knowledge, emergency response skills, and 
                  hazard awareness training.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Target className="h-6 w-6 text-yellow-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Why We Exist</h3>
                </div>
                <p className="text-gray-600 leading-relaxed pl-15">
                  Traditional well control training focuses primarily on drilling teams and certified personnel. However, many others — from 
                  third-party contractors, service company representatives, to rig crew support staff — are often present during active 
                  drilling operations in oil and gas fields without comprehensive safety training.
                </p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 lg:order-1">
            <div className="relative">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 rounded-2xl text-white transform rotate-3 hover:rotate-0 transition-transform">
                    <Users className="h-8 w-8 mb-4" />
                    <h4 className="font-bold text-lg mb-2">Expert Instructors</h4>
                    <p className="text-sm text-orange-100">Industry professionals with real-world drilling experience</p>
                  </div>
                  <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 p-6 rounded-2xl text-white transform -rotate-2 hover:rotate-0 transition-transform">
                    <BookOpen className="h-8 w-8 mb-4" />
                    <h4 className="font-bold text-lg mb-2">Practical Training</h4>
                    <p className="text-sm text-yellow-100">Hands-on simulations and real-world case studies</p>
                  </div>
                </div>
                <div className="space-y-6 mt-12">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-2xl text-white transform rotate-2 hover:rotate-0 transition-transform">
                    <Shield className="h-8 w-8 mb-4" />
                    <h4 className="font-bold text-lg mb-2">Safety First</h4>
                    <p className="text-sm text-blue-100">Comprehensive hazard awareness and emergency response training</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-2xl text-white transform -rotate-1 hover:rotate-0 transition-transform">
                    <Globe className="h-8 w-8 mb-4" />
                    <h4 className="font-bold text-lg mb-2">Global Standards</h4>
                    <p className="text-sm text-green-100">Training aligned with international petroleum industry guidelines</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Philosophy Cards - Masonry Style */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-black text-gray-900 mb-4">
              Our <span className="bg-gradient-to-r from-orange-600 to-yellow-600 bg-clip-text text-transparent">Training Philosophy</span>
            </h3>
            <p className="text-lg text-gray-600">Built on four core principles that ensure effective learning</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {philosophyPoints.map((point, index) => {
              const colors = [
                'from-orange-500 to-red-500',
                'from-yellow-500 to-orange-500', 
                'from-blue-500 to-purple-500',
                'from-green-500 to-teal-500'
              ];
              return (
                <Card key={index} className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group">
                  <div className={`h-2 bg-gradient-to-r ${colors[index]}`}></div>
                  <CardContent className="p-6 text-center">
                    <div className={`w-16 h-16 bg-gradient-to-br ${colors[index]} rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                      <BookOpen className="h-8 w-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">{point.title}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">{point.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Benefits - Bento Grid Style */}
        <div className="bg-gradient-to-br from-slate-900 to-orange-900 rounded-3xl p-8 lg:p-12 text-white">
          <div className="text-center mb-10">
            <h3 className="text-4xl font-black mb-4">
              Benefits of Training <span className="text-orange-300">with Us</span>
            </h3>
            <p className="text-xl text-orange-100">Comprehensive advantages for your team and organization</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-all group">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-6 w-6 text-green-400 group-hover:scale-110 transition-transform" />
                  </div>
                  <p className="text-orange-100 leading-relaxed group-hover:text-white transition-colors">{benefit}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};